import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import {Container} from 'src/common/Container'
import {BenefitsCard} from './components/BenefitsCard'
import {CheckIconVariation} from './components/CheckIconVariation'

type RefreshComparisonSectionProps = {
  slice: Content.ComparisonSectionSliceRefresh
}

export const RefreshComparisonSection = ({slice}: RefreshComparisonSectionProps) => {
  const isGreenBackground = slice.primary.background_color === 'Dark Green'

  return (
    <Container
      backgroundColor={slice.primary.background_color}
      data-slice-type="comparison_section"
      data-slice-variation="refresh"
      className="overflow-visible !py-6 px-5 md:!py-12 md:px-10 lg:px-5 xl:px-0"
    >
      <div className="mx-auto flex w-full max-w-container flex-col">
        {isFilled.keyText(slice.primary.heading) && (
          <h2
            className={cn('mb-10 text-center text-32 font-medium leading-10 tracking-tight text-moss-black md:mb-16', {
              'text-moss-white': isGreenBackground,
            })}
          >
            {slice.primary.heading}
          </h2>
        )}

        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Credit card */}
          <BenefitsCard
            title={slice.primary.credit_title}
            subtitle={slice.primary.credit_subtitle}
            benefits={slice.primary.credit_benefits}
            isGreenBackground={isGreenBackground}
          />

          {/* Debit card */}
          <BenefitsCard
            title={slice.primary.debit_title}
            subtitle={slice.primary.debit_subtitle}
            benefits={slice.primary.debit_benefits}
            isGreenBackground={isGreenBackground}
          />
        </div>
        <div
          className={cn('mt-6 flex flex-col overflow-hidden rounded-lg border  lg:border-moss-white/10', {
            'bg-[#FFFFFF05] border-moss-white/10': isGreenBackground,
            'bg-moss-white border-black/10': !isGreenBackground,
          })}
        >
          <div className="relative flex flex-col lg:flex-row">
            <div className="flex flex-col gap-6 p-6 pb-10 md:px-8 md:pb-8 lg:w-[35%] lg:shrink-0">
              <p
                className={cn('text-24 font-medium leading-[32px] tracking-tight text-moss-black md:leading-normal', {
                  'text-moss-white': isGreenBackground,
                })}
              >
                {slice.primary.both_cards_title}
              </p>
              <div className="flex flex-col gap-3">
                {slice.primary.both_cards_benefits.map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="flex shrink-0 items-start pt-0.5">
                      <CheckIconVariation variant={isGreenBackground ? 'green-bg' : 'grey'} />
                    </div>
                    <p
                      className={cn('text-15 leading-[22px] text-moss-black md:leading-5', {
                        'text-moss-white': isGreenBackground,
                      })}
                    >
                      {item.benefit}
                    </p>
                  </div>
                ))}
              </div>
              {isFilled.keyText(slice.primary.legal_note) && (
                <p
                  className={cn('text-[11px] font-medium leading-none tracking-wide text-moss-grey-400', {
                    'text-[#FFFFFFB3]': isGreenBackground,
                  })}
                >
                  {slice.primary.legal_note}
                </p>
              )}
            </div>

            {isFilled.image(slice.primary.both_cards_image) && (
              <div className="flex h-[240px] items-end justify-center lg:absolute lg:inset-y-0 lg:right-0 lg:h-auto lg:w-[65%] lg:items-end lg:justify-center lg:pt-6">
                <PrismicNextImage
                  field={slice.primary.both_cards_image}
                  className="max-h-full w-auto object-contain object-bottom"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  )
}
